import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import {
  DEFAULT_PAPER_SIZE,
  PRINT_DOCUMENTS,
  normalizePaperSize,
} from '../utils/challanPrintLayout';

/**
 * Per-document paper size, read once per session and cached.
 *
 * Mirrors ChannelSettingsContext. Readable by every role because the size is applied
 * in the browser at print time; only a super admin can change it (enforced by the API).
 *
 * State starts at, and falls back to, A5 — what challans printed at before this
 * setting existed — so a failed fetch or an API that predates the endpoint prints
 * exactly as it always did rather than an unexpected size.
 */

const api = axios.create({ baseURL: `${API_BASE_URL}/api/v1` });
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CACHE_TTL_MS = 60 * 1000;

const DEFAULTS = {
  [PRINT_DOCUMENTS.DELIVERY_CHALLAN]: DEFAULT_PAPER_SIZE,
  [PRINT_DOCUMENTS.PURCHASE_CHALLAN]: DEFAULT_PAPER_SIZE,
};

const PrintSettingsContext = createContext(null);

export const PrintSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef(0);

  const fetchSettings = useCallback(async (force = false) => {
    if (!force && Date.now() - lastFetchRef.current < CACHE_TTL_MS) return;
    try {
      const res = await api.get('/print-settings');
      const map = { ...DEFAULTS };
      (res.data || []).forEach((item) => {
        // hasOwnProperty, not `in`: `in` walks the prototype chain, so a
        // document_type of 'constructor' or 'toString' would read as a known key.
        if (Object.prototype.hasOwnProperty.call(map, item?.document_type)) {
          map[item.document_type] = normalizePaperSize(item.paper_size);
        }
      });
      setSettings(map);
      lastFetchRef.current = Date.now();
    } catch {
      // Keep the A5 defaults on error — an unreachable endpoint must not change
      // what comes out of the printer.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(true); }, [fetchSettings]);

  const updatePrintSetting = useCallback(async (documentType, paperSize) => {
    const res = await api.put(`/print-settings/${documentType}`, { paper_size: paperSize });
    setSettings((prev) => ({
      ...prev,
      [documentType]: normalizePaperSize(res.data?.paper_size),
    }));
    lastFetchRef.current = 0; // invalidate cache
    return res.data;
  }, []);

  return (
    <PrintSettingsContext.Provider value={{
      paperSizes: settings,
      deliveryChallanPaperSize: settings[PRINT_DOCUMENTS.DELIVERY_CHALLAN],
      purchaseChallanPaperSize: settings[PRINT_DOCUMENTS.PURCHASE_CHALLAN],
      loading,
      updatePrintSetting,
      refetch: () => fetchSettings(true),
    }}>
      {children}
    </PrintSettingsContext.Provider>
  );
};

export const usePrintSettings = () => {
  const ctx = useContext(PrintSettingsContext);
  if (!ctx) throw new Error('usePrintSettings must be used inside PrintSettingsProvider');
  return ctx;
};
